import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createAgentThread, getAgentFeedback, getAgentRun, getAgentThread, listAgentThreadRuns, listAgentThreads, resumeAgentRun, streamThreadMessage, updateAgentProjectState, type AgentRunRecord, type AgentThreadSummary } from '../api/agent'
import { langGraphPartEvent, type SseEvent } from '../api/client'
import { AgentContextWindow, AiMessage, AiTask, AgentTimelineEvent, AgentTraceEvent, conversationScopeKey, getActiveTaskId, readTasks, setActiveTaskId, upsertTask, writeTasks } from './taskStore'
import { withThreadRunLock } from './threadRunLock'

export interface AgentConversationOptions {
  /** 已认证账号的键。不同账号之间绝不能串用任务。 */
  scopeKey: string
  /** 当前轮次明确选择的项目；它随请求进入 LangGraph State。 */
  projectId?: string
}

export interface AgentConversation {
  tasks: AiTask[]
  taskId?: string
  threadId?: string
  messages: AiMessage[]
  running: boolean
  /** 当前正在接收 SSE 的任务；切换到其他任务时仍保留，用于避免误显示运行态。 */
  runningTaskId?: string
  notice: string
  send: (value: string) => Promise<void>
  resolveConfirmation: (messageIndex: number, approved: boolean) => Promise<void>
  newChat: () => void
  selectTask: (task: AiTask) => void
  setProjectId: (projectId?: string) => void
  setNotice: (notice: string) => void
  updateAssistant: (update: (message: AiMessage) => AiMessage) => void
  updateMessage: (index: number, update: (message: AiMessage) => AiMessage) => void
}

const TRACE_TYPES = new Set(['context_compaction', 'context_window', 'tool_call', 'tool_result', 'message', 'run_started', 'run_resumed', 'run_interrupted', 'run_completed', 'run_unavailable', 'run_empty', 'run_partial', 'run_error', 'creation_write', 'error'])
const TERMINAL_STATUSES = new Set(['completed', 'unavailable', 'empty', 'partial', 'error', 'interrupted'])
const TERMINAL_EVENT_TYPES = new Set(['run_completed', 'run_unavailable', 'run_empty', 'run_partial', 'run_error', 'run_interrupted', 'error'])
function textValue(value: unknown) { return typeof value === 'string' ? value : '' }
function stableJson(value: unknown) {
  return JSON.stringify(value, (_key, item: unknown) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item
    return Object.fromEntries(Object.entries(item as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right)))
  })
}
function stableId(prefix: string) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return `${prefix}-${crypto.randomUUID()}`
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
function numberValue(value: unknown) { return typeof value === 'number' && Number.isFinite(value) ? value : Number(value) }
function objectValue(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined
}
function workspaceLinks(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return []
  return value.filter(item => {
    const link = objectValue(item)
    return Boolean(link && textValue(link.url).trim() && textValue(link.label).trim())
  }).map(item => objectValue(item) as Record<string, unknown>)
}

function structuredMessageValue(value: string): Record<string, unknown> | Array<unknown> | undefined {
  try {
    const parsed = JSON.parse(value)
    return objectValue(parsed) || (Array.isArray(parsed) ? parsed : undefined)
  } catch {
    return undefined
  }
}

function isRawStructuredMessage(value: string) {
  const parsed = structuredMessageValue(value.trim())
  if (Array.isArray(parsed)) {
    const first = objectValue(parsed[0])
    return Boolean(first && (first.aid || first.bvid || first.cid))
  }
  return Boolean(parsed && !Array.isArray(parsed) && ['topic', 'outline', 'script'].includes(textValue(parsed.kind)))
}

function creationOutput(value: unknown): Record<string, unknown> | undefined {
  const output = objectValue(value)
  if (!output || !['topic', 'outline', 'script'].includes(textValue(output.kind))) return undefined
  return output
}

function isRawAnswer(value: string) {
  return isRawStructuredMessage(value)
}

export function displayAssistantMessage(message: AiMessage) {
  const failed = ['error', 'unavailable', 'empty'].includes(message.status || '')
  if (failed) {
    // 失败状态只显示真实错误，不读取父模型可能留下的 answer 兜底文本。
    for (const trace of [...(message.trace || [])].reverse()) {
      const output = objectValue(trace.output)
      const error = textValue(output?.error).trim()
      if (error) return error
    }
    return isRawStructuredMessage(message.text) ? '' : message.text
  }
  const directAnswer = textValue(message.creation?.answer).trim()
  if (directAnswer && !isRawAnswer(directAnswer)) return directAnswer
  for (const trace of [...(message.trace || [])].reverse()) {
    const output = objectValue(trace.output)
    if (!output) continue
    const answer = textValue(output.answer) || textValue(output.text) || textValue(output.message)
    if (answer.trim() && !isRawAnswer(answer)) return answer.trim()
  }
  if (isRawStructuredMessage(message.text)) {
    // 结构化业务对象直接渲染卡片；没有模型明确文本时不猜测摘要。
    return ''
  }
  return message.text
}

const AgentConversationContext = createContext<AgentConversation | undefined>(undefined)

export function AgentConversationProvider({ value, children }: { value: AgentConversation; children: ReactNode }) {
  return createElement(AgentConversationContext.Provider, { value }, children)
}

export function useAgentConversation(): AgentConversation {
  const conversation = useContext(AgentConversationContext)
  if (!conversation) throw new Error('useAgentConversation must be used inside AgentConversationProvider')
  return conversation
}
function contextWindow(value: unknown): AgentContextWindow | undefined {
  const item = objectValue(value)
  if (!item) return undefined
  const usedTokens = numberValue(item.used_tokens)
  const limitTokens = numberValue(item.limit_tokens)
  const remainingTokens = numberValue(item.remaining_tokens)
  const usedPercent = numberValue(item.used_percent)
  if (![usedTokens, limitTokens, remainingTokens, usedPercent].every(Number.isFinite)) return undefined
  return {
    usedTokens: Math.max(0, usedTokens),
    limitTokens: Math.max(0, limitTokens),
    remainingTokens: Math.max(0, remainingTokens),
    usedPercent: Math.min(100, Math.max(0, usedPercent)),
    source: 'summarize',
    mode: 'dev',
  }
}
function workflowStatus(value: unknown): AiMessage['status'] | undefined {
  const status = textValue(value).toLowerCase()
  if (status === 'running' || status === 'completed' || status === 'unavailable' || status === 'empty' || status === 'partial' || status === 'error' || status === 'interrupted') return status
  return undefined
}
function pendingConfirmation(value: unknown): AiMessage['pendingConfirmation'] | undefined {
  const items = Array.isArray(value) ? value : value ? [value] : []
  for (const item of items) {
    const wrapper = objectValue(item)
    const detail = objectValue(wrapper?.value) || wrapper
    if (detail?.type !== 'tool_confirmation_required') continue
    const tools = Array.isArray(detail.tools)
      ? detail.tools.map(tool => objectValue(tool)).filter(Boolean).map(tool => ({ name: textValue(tool?.name), id: textValue(tool?.id), args: objectValue(tool?.args) })).filter(tool => tool.name && tool.id)
      : []
    return { type: 'tool_confirmation_required', message: textValue(detail.message) || '确认执行这些操作吗？', tools }
  }
  return undefined
}
function traceLabel(type: string, trace: AgentTraceEvent) {
  if (type === 'context_compaction') return trace.summary || trace.detail || ''
  if (type === 'tool_call') {
    if (trace.summary) return trace.summary
    const names = trace.tools?.length ? trace.tools.join('、') : trace.tool
    return names ? `正在${names}` : '正在调用工具'
  }
  if (type === 'tool_result') {
    if (trace.status === 'error') return trace.summary || '工具调用失败'
    return trace.summary || ''
  }
  if (type === 'run_resumed') return '已恢复运行'
  if (type === 'run_interrupted') return '等待确认后继续'
  if (type === 'creation_write') {
    if (trace.status === 'running') return '正在写入素材库'
    if (trace.status === 'written') return '已写入素材库'
    if (trace.status === 'error') return '写入素材库失败'
    return trace.detail || '正在写入素材库'
  }
  if (type === 'error') return trace.detail || '运行出现错误'
  return ''
}

function appendTimeline(message: AiMessage, item: AgentTimelineEvent): AiMessage {
  const timeline = message.timeline || []
  if (item.kind === 'answer' && timeline.some(existing => existing.kind === 'answer')) return message
  return { ...message, timeline: [...timeline, item] }
}

function appendTrace(message: AiMessage, item: AgentTimelineEvent): AiMessage {
  const trace = item.trace
  const ids = trace?.toolCallIds || []
  if (trace && ids.length && (trace.type === 'tool_call' || trace.type === 'tool_result')) {
    const duplicate = (message.timeline || []).some(existing => {
      const existingTrace = existing.kind === 'trace' ? existing.trace : undefined
      if (!existingTrace || existingTrace.type !== trace.type) return false
      return (existingTrace.toolCallIds || []).some(id => ids.includes(id))
    })
    if (duplicate) return message
  }
  if (trace && (trace.type === 'tool_call' || trace.type === 'tool_result')) {
    const descriptor = stableJson({ tool: trace.tool, tools: trace.tools, input: trace.input, output: trace.output })
    const duplicate = (message.timeline || []).some(existing => {
      const existingTrace = existing.kind === 'trace' ? existing.trace : undefined
      if (!existingTrace || existingTrace.type !== trace.type || (existingTrace.toolCallIds || []).length || ids.length) return false
      return stableJson({ tool: existingTrace.tool, tools: existingTrace.tools, input: existingTrace.input, output: existingTrace.output }) === descriptor
    })
    if (duplicate) return message
  }
  return appendTimeline(message, item)
}

function appendReasoning(message: AiMessage, text: string, seq: number): AiMessage {
  const timeline = message.timeline || []
  const last = timeline[timeline.length - 1]
  if (last?.kind === 'reasoning') {
    return { ...message, timeline: [...timeline.slice(0, -1), { ...last, text: `${last.text || ''}${text}` }] }
  }
  return appendTimeline(message, { kind: 'reasoning', seq, text })
}

function appendAnswer(message: AiMessage, seq: number, text = ''): AiMessage {
  const timeline = message.timeline || []
  if (!text) return message
  const lastIndex = timeline.length - 1
  if (timeline[lastIndex]?.kind !== 'answer') {
    return { ...message, timeline: [...timeline, { kind: 'answer', seq, text }] }
  }
  return {
    ...message,
    timeline: timeline.map((item, index) => index === lastIndex ? { ...item, text: `${item.text || ''}${text}` } : item),
  }
}

/** 将传输事件转换为用户可安全查看、易于理解的执行轨迹。 */
export function consumeAgentEvent(message: AiMessage, event: SseEvent): AiMessage {
  const type = event.type
  if (!TRACE_TYPES.has(type)) return message
  const eventMetadata = objectValue(event.metadata)
  const checkpointNamespace = textValue(event.langgraph_checkpoint_ns) || textValue(eventMetadata?.langgraph_checkpoint_ns)
  if (type === 'message' && checkpointNamespace.startsWith('execute_tools|')) return message
  // 用户输入请求代表当前回合已经结束；下一条普通消息会创建新的 run。
  if (TERMINAL_STATUSES.has(message.status || '') && !TERMINAL_EVENT_TYPES.has(type) && type !== 'run_resumed') return message
  const output = objectValue(event.output)
  const result = output || {}
  // 只把终态 output 作为三个内容子图的最终业务对象；工具事件本身仍只进入原有事件流。
  const creation = TERMINAL_EVENT_TYPES.has(type) ? creationOutput(result) : undefined
  const now = Date.now()
  const next = { ...message, runStartedAt: message.runStartedAt || now }
  const window = contextWindow(event.context_window)
  if (window) next.contextWindow = window
  const runId = textValue(event.run_id) || textValue(result.run_id)
  if (runId) next.runId = runId
  if (type === 'run_started') {
    const assistantMessageId = textValue(event.assistant_message_id)
    if (!assistantMessageId) {
      return { ...next, error: true, retryable: false, status: 'error', text: '服务端协议错误：run_started 缺少 assistant_message_id' }
    }
    next.id = assistantMessageId
  } else if (textValue(event.assistant_message_id)) {
    next.id = textValue(event.assistant_message_id)
  }
  const status = type === 'run_started'
    ? workflowStatus(event.status) || 'running'
    : TERMINAL_EVENT_TYPES.has(type)
      ? workflowStatus(result.status) || workflowStatus(event.status) || (type === 'error' || type === 'run_error' ? 'error' : type === 'run_unavailable' ? 'unavailable' : type === 'run_empty' ? 'empty' : type === 'run_partial' ? 'partial' : type === 'run_completed' ? 'completed' : undefined)
      : undefined
  const unsuccessfulTerminal = status === 'unavailable' || status === 'empty' || status === 'partial' || status === 'error'
  const summary = textValue(event.summary) || textValue(event.detail)
  // SSE id 只在单次连接内递增；展示时间线使用本地追加序号，避免不同回合
  // 的事件编号碰撞或被错误去重。
  const sequence = next.timeline?.length || 0
  const trace: AgentTraceEvent = {
    type,
    message: textValue(event.message) || undefined,
    summary: summary || undefined,
    detail: textValue(event.detail) || undefined,
    node: textValue(event.node) || undefined,
    status: textValue(event.status) || undefined,
    source: textValue(event.source) || undefined,
    tool: textValue(event.tool) || undefined,
    tools: Array.isArray(event.tools) ? event.tools.map(textValue).filter(Boolean) : undefined,
    toolCallIds: Array.isArray(event.tool_call_ids) ? event.tool_call_ids.map(textValue).filter(Boolean) : undefined,
    input: event.input,
    output: event.output,
    subgraph: textValue(event.subgraph) || undefined,
    at: now,
  }
  const content = textValue(event.content)
  const isMessageChunk = type === 'message' && textValue(event.stream_type) === 'messages'
  const reasoningDelta = textValue(event.reasoning_delta)
  if (isMessageChunk && reasoningDelta) {
    const timelineMessage = appendReasoning(next, reasoningDelta, sequence)
    next.timeline = timelineMessage.timeline
  }
  if (isMessageChunk && content) {
    // messages 通道承载 content_blocks 中的 text；业务结构化结果走 output 字段。
    next.text = `${next.text || ''}${content}`
    next.timeline = appendAnswer(next, sequence, content).timeline
  }
  const successTrace = type === 'run_completed'
  if (!isMessageChunk && type !== 'message' && !(unsuccessfulTerminal && successTrace)) {
    const label = traceLabel(type, trace)
    if (label) {
      const traceItem = { ...trace, label }
      next.trace = [...(next.trace || []), traceItem]
      const timelineMessage = appendTrace(next, { kind: 'trace', seq: sequence, trace: traceItem })
      next.timeline = timelineMessage.timeline
    }
  }
  if (unsuccessfulTerminal) {
    next.trace = (next.trace || []).filter(item => item.type !== 'run_completed')
    next.timeline = (next.timeline || []).filter(item => item.kind !== 'answer')
  }
  const finished = TERMINAL_EVENT_TYPES.has(type) || ['unavailable', 'empty', 'partial', 'error', 'interrupted'].includes(status || '')
  if (finished) {
    next.runFinishedAt = now
    next.runDurationMs = Math.max(0, now - (next.runStartedAt || now))
  }
  if (status && !(next.status && ['error', 'unavailable', 'empty'].includes(next.status) && status === 'completed')) next.status = status
  const confirmation = pendingConfirmation(event.interrupts || result.interrupts)
  if (confirmation) {
    next.pendingConfirmation = confirmation
    next.status = 'interrupted'
    next.error = false
    next.retryable = false
    next.text = confirmation.message
  } else if (type === 'run_resumed' || status === 'completed') {
    next.pendingConfirmation = undefined
  }
  const topics = Array.isArray(result.topics) ? result.topics.filter(item => objectValue(item)) as Array<Record<string, unknown>> : undefined
  if (topics?.length) {
    next.topics = topics
  }
  if (creation) next.creation = creation
  const studioWrite = objectValue(result.studio_write)
  const studioWriteStatus = textValue(studioWrite?.status)
  if (studioWrite && studioWriteStatus === 'written') next.studioWrite = studioWrite
  const links = workspaceLinks(result.workspace_links || event.workspace_links)
  if (links.length) {
    const existing = next.workspaceLinks || []
    const merged = [...existing, ...links].filter((item, index, all) => {
      const url = textValue(item.url)
      return url && index === all.findIndex(other => textValue(other.url) === url)
    })
    next.workspaceLinks = merged
  }
  const terminalFailure = type === 'error' || Boolean(event.error) || ['unavailable', 'empty', 'error'].includes(status || '')
  const rawResponse = terminalFailure
    ? result.error || event.error || result.message || event.message || status
    : result.text || result.answer || result.message || event.message
  const response = textValue(rawResponse)
  if (type === 'run_completed') {
    next.text = response || (isRawStructuredMessage(next.text) ? '' : next.text)
    if (next.text) next.timeline = appendAnswer(next, sequence).timeline
  }
  if (type === 'error' || type === 'run_error' || event.error) {
    next.error = true
    next.status = 'error'
    next.retryable = Boolean(event.retryable ?? result.retryable)
    next.text = response || textValue(event.error) || (isRawStructuredMessage(next.text) ? '' : next.text)
  }
  if (type === 'run_completed' && status === 'error') {
    next.error = true
    next.status = 'error'
    next.retryable = Boolean(result.retryable ?? true)
    next.text = response || textValue(result.error) || (isRawStructuredMessage(next.text) ? '' : next.text)
  }
  if (status === 'unavailable') {
    next.error = true
    next.retryable = true
    next.text = response || (isRawStructuredMessage(next.text) ? '' : next.text)
  } else if (status === 'empty') {
    next.error = true
    next.text = response || (isRawStructuredMessage(next.text) ? '' : next.text)
  } else if (status === 'partial' && !next.text) {
    next.text = response || 'AI 仅返回部分结果，请补充后再确认写入。'
  }
  return next
}

function runForMessage(raw: Record<string, unknown>, runs: AgentRunRecord[]) {
  const runId = textValue(raw.run_id)
  return runId ? runs.find(run => run.run_id === runId) : undefined
}

function applyPersistedRun(message: AiMessage, run: AgentRunRecord) {
  let next: AiMessage = {
    ...message,
    text: '',
    trace: [],
    timeline: [],
    status: undefined,
    error: undefined,
    pendingConfirmation: undefined,
  }
  for (const [sequence, item] of (run.events || []).entries()) {
    // 运行记录保存的是 AgentEvent envelope；实时 SSE 才是 LangGraph StreamPart。
    // 这里只还原展示投影，不改变 Agent 执行链路。
    const event = langGraphPartEvent(item, sequence) || (() => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return undefined
      const persisted = item as Record<string, unknown>
      const eventType = textValue(persisted.event)
      const payload = objectValue(persisted.payload)
      return eventType && payload ? { ...payload, type: eventType, node: textValue(persisted.node), seq: sequence } : undefined
    })()
    if (!event) continue
    next = consumeAgentEvent(next, {
      ...event,
      run_id: run.run_id,
      ...(event.type === 'run_started' && next.id ? { assistant_message_id: next.id } : {}),
    })
  }
  return next
}

function applyRunTiming(message: AiMessage, run?: AgentRunRecord) {
  if (!run) return message
  const startedAt = Date.parse(run.created_at || '')
  const finishedAt = ['completed', 'unavailable', 'empty', 'partial', 'error', 'interrupted'].includes(run.status) ? Date.parse(run.updated_at || '') : NaN
  const next = { ...message }
  if (Number.isFinite(startedAt)) next.runStartedAt = startedAt
  if (Number.isFinite(finishedAt)) {
    next.runFinishedAt = finishedAt
    if (Number.isFinite(startedAt)) next.runDurationMs = Math.max(0, finishedAt - startedAt)
  }
  return next
}

function serverMessage(raw: Record<string, unknown>, runs: AgentRunRecord[]): AiMessage | undefined {
  const role = raw.role === 'assistant' ? 'assistant' : raw.role === 'user' ? 'user' : undefined
  if (!role) return undefined
  const id = textValue(raw.id)
  if (!id) return undefined
  if (role === 'user') return { id, role, text: textValue(raw.content), syncState: 'synced' }
  const run = runForMessage(raw, runs)
  const output = objectValue(raw.output) || objectValue(run?.output) || {}
  const status = workflowStatus(output.status) || workflowStatus(run?.status) || (raw.error ? 'error' : undefined)
  const rawText = textValue(raw.content)
  const completedCreation = status === 'completed' ? creationOutput(output) : undefined
  let message: AiMessage = { id, role, text: '', trace: [], timeline: [], status, syncState: 'synced', runId: textValue(raw.run_id) || run?.run_id, creation: completedCreation }
  if (run) message = applyPersistedRun(message, run)
  // 没有运行记录时，保留服务端投影中的普通助手文本。
  if (rawText && !message.text && !isRawStructuredMessage(rawText)) {
    message.text = rawText
    message.timeline = appendAnswer(message, message.timeline?.length || 0).timeline
  }
  if (raw.error || run?.error) message = { ...message, error: true, retryable: true, text: textValue(raw.error) || textValue(run?.error) || message.text }
  message = applyRunTiming(message, run)
  return message
}

function applyServerFeedback(messages: AiMessage[], rows: Awaited<ReturnType<typeof getAgentFeedback>>): AiMessage[] {
  const byIndex = new Map(rows.map(row => [row.message_index, row]))
  return messages.map((message, index) => {
    const row = byIndex.get(index)
    return { ...message, feedback: row ? { rating: row.rating, ...(row.reason ? { reason: row.reason } : {}), ...(row.other_reason ? { otherReason: row.other_reason } : {}) } : undefined }
  }) as AiMessage[]
}

function applyServerRuns(messages: AiMessage[], runs: AgentRunRecord[]) {
  return messages.map(message => {
    if (message.role !== 'assistant' || !message.runId) return message
    const run = runs.find(item => item.run_id === message.runId)
    if (!run) return message
    let next = applyPersistedRun({ ...message, status: workflowStatus(run.status) || message.status }, run)
    next = applyRunTiming(next, run)
    // 运行记录可能只有 output、没有逐事件明细；服务端终态仍然是权威状态。
    const status = workflowStatus(run.status)
    if (status) next.status = status
    const output = objectValue(run.output)
    const answer = textValue(output?.answer) || textValue(output?.text) || textValue(output?.message)
    if (answer && !next.text && !['unavailable', 'empty', 'error'].includes(status || '')) {
      next.text = answer
      next.timeline = appendAnswer(next, next.timeline?.length || 0).timeline
    }
    return next
  })
}

function applyServerRun(message: AiMessage, run: AgentRunRecord) {
  return applyServerRuns([{ ...message, runId: run.run_id }], [run])[0]
}

async function hydrateServerTask(thread: AgentThreadSummary): Promise<AiTask> {
  const [feedback, runs] = await Promise.all([
    getAgentFeedback(thread.thread_id),
    listAgentThreadRuns(thread.thread_id),
  ])
  const messages = (thread.messages || []).map(raw => serverMessage(raw, runs)).filter(Boolean) as AiMessage[]
  const hydratedMessages = applyServerFeedback(messages, feedback)
  const firstUser = hydratedMessages.find(message => message.role === 'user')
  return {
    id: `thread:${thread.thread_id}`,
    threadId: thread.thread_id,
    title: firstUser?.text.slice(0, 28) || '服务端对话',
    messages: hydratedMessages,
    updatedAt: Date.parse(thread.updated_at || thread.created_at || '') || Date.now(),
  }
}

function messageIdentity(message: AiMessage) {
  return message.id
}

function mergeMessageState(remote: AiMessage, local: AiMessage): AiMessage {
  const remoteHasTimeline = Boolean(remote.timeline?.length)
  const remoteHasTrace = Boolean(remote.trace?.length)
  const remoteTerminal = Boolean(remote.runId && TERMINAL_STATUSES.has(remote.status || ''))
  return {
    ...local,
    ...remote,
    id: remote.id,
    syncState: 'synced',
    runId: remote.runId || local.runId,
    status: remote.status || local.status,
    error: remote.error ?? local.error,
    retryable: remote.retryable ?? local.retryable,
    pendingConfirmation: remote.pendingConfirmation || local.pendingConfirmation,
    text: remoteTerminal ? remote.text : (isRawStructuredMessage(local.text) ? remote.text : (remote.text.length >= local.text.length ? remote.text : local.text)),
    trace: remoteTerminal ? remote.trace : (remoteHasTrace && remote.trace!.length >= (local.trace?.length || 0) ? remote.trace : local.trace),
    timeline: remoteTerminal ? remote.timeline : (remoteHasTimeline && remote.timeline!.length >= (local.timeline?.length || 0) ? remote.timeline : local.timeline),
    topics: remote.topics?.length ? remote.topics : local.topics,
    creation: remote.creation || (remoteTerminal ? undefined : local.creation),
    workspaceLinks: remote.workspaceLinks?.length ? remote.workspaceLinks : local.workspaceLinks,
    feedback: remote.feedback || local.feedback,
  }
}

function mergeTaskMessages(remote: AiMessage[], local: AiMessage[]) {
  const localById = new Map(local.filter(message => message.id).map(message => [message.id, message]))
  const merged = remote.map(remoteMessage => {
    const localMessage = localById.get(remoteMessage.id)
    return localMessage ? mergeMessageState(remoteMessage, localMessage) : remoteMessage
  })
  const remoteIds = new Set(remote.map(messageIdentity))
  // 只保留当前代码创建、明确标记为 pending 的本地消息；不迁移旧缓存，也不按位置/角色猜测归属。
  local.forEach(localMessage => {
    if (localMessage.syncState === 'pending' && !remoteIds.has(messageIdentity(localMessage))) merged.push(localMessage)
  })
  return merged
}

function mergeRemoteTasks(local: AiTask[], remote: AiTask[]): AiTask[] {
  const matched = new Set<number>()
  const findRemoteIndex = (task: AiTask) => remote.findIndex((item, index) => {
    if (matched.has(index)) return false
    return item.id === task.id || Boolean(task.threadId && item.threadId === task.threadId)
  })
  const mergeTask = (cached: AiTask, task: AiTask) => ({
    ...task,
    // 保留本地任务 ID；threadId 才是服务端身份，刷新后不能让选中态漂移。
    id: cached.id || task.id,
    title: cached.title || task.title,
    messages: mergeTaskMessages(task.messages, cached.messages),
    updatedAt: Math.max(task.updatedAt, cached.updatedAt),
  })
  // 服务端回读只更新任务内容，不重新排列已有条目，避免异步回读期间点击位置漂移。
  const merged = local.map(task => {
    const remoteIndex = findRemoteIndex(task)
    if (remoteIndex < 0) return task
    matched.add(remoteIndex)
    return mergeTask(task, remote[remoteIndex])
  })
  remote.forEach((task, index) => {
    if (!matched.has(index)) merged.push(task)
  })
  return merged.slice(0, 12)
}

export function useAgentConversationState({ scopeKey, projectId }: AgentConversationOptions): AgentConversation {
  const storageScopeKey = conversationScopeKey(scopeKey)
  const [tasks, setTasks] = useState<AiTask[]>(() => readTasks(storageScopeKey))
  const [hydratedScopeKey, setHydratedScopeKey] = useState(storageScopeKey)
  const [taskId, setTaskId] = useState<string>()
  const [threadId, setThreadId] = useState<string>()
  const [messages, setMessages] = useState<AiMessage[]>([])
  const [running, setRunning] = useState(false)
  const [runningTaskId, setRunningTaskId] = useState<string>()
  const [notice, setNotice] = useState('')
  const [activeProjectId, setActiveProjectId] = useState(projectId)
  const persistedSnapshotRef = useRef('')
  const loadedScopeRef = useRef(storageScopeKey)
  const tasksRef = useRef<AiTask[]>([])
  const messagesRef = useRef<AiMessage[]>([])
  const threadRef = useRef<string | undefined>(undefined)
  const taskIdRef = useRef<string | undefined>(undefined)
  const runningRef = useRef(false)
  const activeRequestRef = useRef<string | undefined>(undefined)
  const activeRunMessagesRef = useRef<AiMessage[]>([])
  const refreshRevisionRef = useRef(0)
  const hydrationRevisionRef = useRef(0)
  const persistingRef = useRef(false)
  const requestedProjectStateRef = useRef<string | undefined>(undefined)
  useEffect(() => { tasksRef.current = tasks }, [tasks])
  useEffect(() => { messagesRef.current = messages }, [messages])
  useEffect(() => { threadRef.current = threadId }, [threadId])
  useEffect(() => { taskIdRef.current = taskId }, [taskId])
  useEffect(() => { runningRef.current = running }, [running])
  useEffect(() => { setActiveProjectId(projectId) }, [projectId])

  useEffect(() => {
    const hydrationRevision = hydrationRevisionRef.current + 1
    hydrationRevisionRef.current = hydrationRevision
    loadedScopeRef.current = storageScopeKey
    persistedSnapshotRef.current = ''
    const stored = readTasks(storageScopeKey)
    setTasks(stored)
    const activeId = getActiveTaskId(storageScopeKey)
    const active = activeId ? stored.find(item => item.id === activeId) : undefined
    setTaskId(active?.id)
    setThreadId(active?.threadId)
    setMessages(active?.messages || [])
    setHydratedScopeKey(storageScopeKey)
    setNotice('')
    let cancelled = false
    void listAgentThreads().then(async summaries => {
      if (!Array.isArray(summaries)) return
      const details = await Promise.all(summaries.slice(0, 24).map(summary => getAgentThread(summary.thread_id).catch(() => undefined)))
      const scoped = details.filter((thread): thread is AgentThreadSummary => Boolean(thread)).slice(0, 12)
      const remote = (await Promise.all(scoped.map(thread => hydrateServerTask(thread).catch(() => undefined)))).filter(Boolean) as AiTask[]
      if (cancelled || loadedScopeRef.current !== storageScopeKey || hydrationRevisionRef.current !== hydrationRevision) return
      // 首次恢复请求不能覆盖正在接收 SSE 的当前任务；本地快照和请求回调会继续维护它。
      if (runningRef.current) return
      const currentStored = readTasks(storageScopeKey)
      const liveTasks = tasksRef.current.length ? tasksRef.current : stored
      const localTasks = mergeRemoteTasks(stored, currentStored)
      const liveTasksMerged = mergeRemoteTasks(localTasks, liveTasks)
      const merged = mergeRemoteTasks(liveTasksMerged, remote)
      writeTasks(merged, storageScopeKey)
      setTasks(merged)
      const currentActiveId = getActiveTaskId(storageScopeKey) || taskIdRef.current || activeId
      const cachedActive = currentActiveId ? merged.find(item => item.id === currentActiveId || item.threadId === currentActiveId) : undefined
      const selected = cachedActive || merged[0]
      setTaskId(selected?.id)
      setActiveTaskId(selected?.id, storageScopeKey)
      setThreadId(selected?.threadId)
      setMessages(selected?.messages || [])
    }).catch(() => { /* 服务不可用时保留隔离的本地缓存 */ })
    return () => { cancelled = true }
  }, [storageScopeKey])

  useEffect(() => {
    if (!threadId || running || hydratedScopeKey !== storageScopeKey) return
    const key = `${threadId}:${activeProjectId || ''}`
    if (requestedProjectStateRef.current === key) return
    requestedProjectStateRef.current = key
    void updateAgentProjectState(threadId, activeProjectId).catch(() => {
      // 下一次发送仍会把当前 project_id 和完整项目快照写入 State。
    })
  }, [activeProjectId, hydratedScopeKey, running, storageScopeKey, threadId])

  useEffect(() => {
    const sync = (event: Event) => {
      if (persistingRef.current) return
      const detail = (event as CustomEvent<{ scopeKey?: string }>).detail
      if (detail?.scopeKey && detail.scopeKey !== storageScopeKey) return
      const next = readTasks(storageScopeKey)
      const mergedTasks = mergeRemoteTasks(next, tasksRef.current)
      const tasksChanged = stableJson(mergedTasks) !== stableJson(tasksRef.current)
      if (tasksChanged) setTasks(mergedTasks)
      if (!runningRef.current && taskIdRef.current) {
        const active = mergedTasks.find(item => item.id === taskIdRef.current || item.threadId === threadRef.current)
        if (active) {
          if (active.threadId !== threadRef.current) setThreadId(active.threadId)
          const currentTask = tasksRef.current.find(item => item.id === taskIdRef.current || (threadRef.current && item.threadId === threadRef.current))
          const mergedMessages = currentTask ? mergeTaskMessages(active.messages, currentTask.messages) : active.messages
          const messagesChanged = stableJson(mergedMessages) !== stableJson(messagesRef.current)
          if (messagesChanged) setMessages(mergedMessages)
        }
      }
    }
    window.addEventListener('biliup:ai-state', sync)
    window.addEventListener('storage', sync)
    return () => { window.removeEventListener('biliup:ai-state', sync); window.removeEventListener('storage', sync) }
  }, [running, storageScopeKey, taskId])

  useEffect(() => {
    if (!threadId || running) return
    let cancelled = false
    const refresh = async () => {
      const refreshRevision = ++refreshRevisionRef.current
      try {
        const thread = await getAgentThread(threadId)
        const remote = await hydrateServerTask(thread)
        if (cancelled || refreshRevision !== refreshRevisionRef.current || runningRef.current || threadRef.current !== threadId) return
        setTasks(current => mergeRemoteTasks(current, [remote]))
        setMessages(current => {
          if (refreshRevision !== refreshRevisionRef.current || runningRef.current || threadRef.current !== threadId) return current
          return mergeTaskMessages(remote.messages, current)
        })
      } catch { /* 持久化数据加载不可用时，本地缓存仍可使用 */ }
    }
    void refresh()
    const onFocus = () => { void refresh() }
    window.addEventListener('focus', onFocus)
    return () => { cancelled = true; window.removeEventListener('focus', onFocus) }
  }, [running, threadId])

  const persist = useCallback((nextMessages: AiMessage[], id: string, title: string, activeThreadId?: string) => {
    const snapshot = stableJson({ id, title, messages: nextMessages, threadId: activeThreadId })
    if (persistedSnapshotRef.current === snapshot) return
    persistedSnapshotRef.current = snapshot
    const task: AiTask = { id, title, messages: nextMessages, updatedAt: Date.now(), threadId: activeThreadId }
    persistingRef.current = true
    try {
      setTasks(upsertTask(task, storageScopeKey))
    } finally {
      persistingRef.current = false
    }
  }, [storageScopeKey])

  useEffect(() => {
    if (hydratedScopeKey !== storageScopeKey || !taskId || !messages.length) return
    persist(messages, taskId, messages.find(item => item.role === 'user')?.text.slice(0, 28) || '新对话', threadId)
  }, [hydratedScopeKey, messages, persist, running, storageScopeKey, taskId, threadId])

  const newChat = useCallback(() => {
    hydrationRevisionRef.current += 1
    taskIdRef.current = undefined
    threadRef.current = undefined
    if (!activeRequestRef.current) {
      runningRef.current = false
      setRunning(false)
      setRunningTaskId(undefined)
    }
    setTaskId(undefined); setActiveTaskId(undefined, storageScopeKey); setThreadId(undefined); setMessages([]); setNotice('')
  }, [storageScopeKey])

  const selectTask = useCallback((task: AiTask) => {
    hydrationRevisionRef.current += 1
    taskIdRef.current = task.id
    threadRef.current = task.threadId
    setTaskId(task.id); setActiveTaskId(task.id, storageScopeKey); setThreadId(task.threadId); setMessages(task.messages); setNotice('')
  }, [storageScopeKey])

  const send = useCallback(async (value: string) => {
    const text = value.trim()
    if (!text || runningRef.current) return
    runningRef.current = true
    refreshRevisionRef.current += 1
    const sameScope = hydratedScopeKey === storageScopeKey
    const currentMessages = sameScope ? messages : []
    const currentTaskId = sameScope ? taskId : undefined
    const currentThreadId = sameScope ? threadId : undefined
    const id = currentTaskId || `conversation-${Date.now()}`
    const requestId = stableId('request')
    const userMessageId = stableId('user')
    const startedAt = Date.now()
    const lockName = `${storageScopeKey}:${currentThreadId || currentTaskId || 'new-thread'}`
    const acquired = await withThreadRunLock(lockName, requestId, async () => {
      hydrationRevisionRef.current += 1
      const initial: AiMessage[] = [...currentMessages, { id: userMessageId, role: 'user', text, syncState: 'pending' }, { id: stableId('assistant'), role: 'assistant', text: '', syncState: 'pending', status: 'running', runStartedAt: startedAt }]
      activeRequestRef.current = requestId
      activeRunMessagesRef.current = initial
      taskIdRef.current = id
      threadRef.current = currentThreadId
      setTaskId(id); setActiveTaskId(id, storageScopeKey); setMessages(initial); setNotice(''); setRunning(true); setRunningTaskId(id)
      persist(initial, id, text.slice(0, 28), currentThreadId)
      let activeThreadId = currentThreadId
      let activeRunId: string | undefined
      const updateRunSnapshot = (nextMessages: AiMessage[]) => {
        activeRunMessagesRef.current = nextMessages
        persist(nextMessages, id, text.slice(0, 28), activeThreadId)
        if (taskIdRef.current === id) setMessages(nextMessages)
      }
      try {
        if (!activeThreadId) {
          const thread = await createAgentThread()
          if (activeRequestRef.current !== requestId) return
          activeThreadId = thread.thread_id
          threadRef.current = activeThreadId
          if (taskIdRef.current === id) setThreadId(activeThreadId)
          persist(initial, id, text.slice(0, 28), activeThreadId)
        }
        const outcome = await streamThreadMessage(activeThreadId, { user_message: text, message_id: userMessageId, project_id: activeProjectId }, event => {
          if (activeRequestRef.current !== requestId) return
          if (event.thread_id) { activeThreadId = String(event.thread_id); threadRef.current = activeThreadId; if (taskIdRef.current === id) setThreadId(activeThreadId) }
          if (event.run_id) activeRunId = String(event.run_id)
          const next = [...activeRunMessagesRef.current]
          const target = next[next.length - 1]
          if (!target || target.role !== 'assistant') return
          next[next.length - 1] = consumeAgentEvent(target, event)
          updateRunSnapshot(next)
        })
        if (activeRequestRef.current !== requestId) return
        if (outcome === 'completed') {
          if (activeRunId) {
            try {
              const durableRun = await getAgentRun(activeRunId)
              if (activeRequestRef.current !== requestId) return
              const next = [...activeRunMessagesRef.current]
              const target = next[next.length - 1]
              if (target?.role === 'assistant') {
                next[next.length - 1] = applyServerRun(target, durableRun)
                updateRunSnapshot(next)
              }
            } catch { /* 对当前实时连接而言，SSE 投影仍是权威数据 */ }
          }
          // 无法从 SSE 取得 run_id 时也必须结束本地运行态，避免切换线程后永久显示“运行中”。
          if (activeRequestRef.current !== requestId) return
          const next = [...activeRunMessagesRef.current]
          const target = next[next.length - 1]
          if (target?.role === 'assistant' && target.status === 'running') {
            const finishedAt = Date.now()
            next[next.length - 1] = {
              ...target,
              status: 'completed',
              runFinishedAt: finishedAt,
              runDurationMs: Math.max(0, finishedAt - (target.runStartedAt || finishedAt)),
            }
            updateRunSnapshot(next)
          }
        }
        if (activeRequestRef.current !== requestId) return
      } catch (error) {
        if (activeRequestRef.current !== requestId) return
        const message = error instanceof Error ? error.message : 'AI 工作流不可用'
        const next = [...activeRunMessagesRef.current]
        const target = next[next.length - 1]
        if (target?.role === 'assistant') {
          const finishedAt = Date.now()
          next[next.length - 1] = { ...target, error: true, retryable: true, status: 'error', text: message, runFinishedAt: finishedAt, runDurationMs: Math.max(0, finishedAt - (target.runStartedAt || finishedAt)) }
          updateRunSnapshot(next)
        }
        if (taskIdRef.current === id) setNotice('运行失败，可重试本条任务。')
      } finally {
        if (activeRequestRef.current === requestId) {
          activeRequestRef.current = undefined
          activeRunMessagesRef.current = []
          runningRef.current = false
          setRunning(false)
          setRunningTaskId(undefined)
        }
      }
    })
    if (!acquired) {
      runningRef.current = false
      setNotice('此对话正在另一个标签页运行，请等待完成后再发送。')
    }
  }, [activeProjectId, hydratedScopeKey, messages, persist, running, storageScopeKey, taskId, threadId])

  const resolveConfirmation = useCallback(async (messageIndex: number, approved: boolean) => {
    const target = messages[messageIndex]
    if (!target?.runId || !target.pendingConfirmation || running) return
    const runId = target.runId
    setRunning(true)
    setNotice(approved ? '已确认，正在恢复同一任务…' : '已拒绝，正在结束同一任务…')
    setMessages(current => current.map((message, index) => index === messageIndex
      ? { ...message, text: '', status: 'running', pendingConfirmation: undefined, error: false, runStartedAt: Date.now(), runFinishedAt: undefined, runDurationMs: undefined }
      : message))
    try {
      const outcome = await resumeAgentRun(runId, approved, event => {
        setMessages(current => current.map((message, index) => index === messageIndex
          ? consumeAgentEvent(message, { ...event, run_id: event.run_id || runId })
          : message))
      })
      if (outcome === 'completed') {
        try {
          const durableRun = await getAgentRun(runId)
          setMessages(current => current.map((message, index) => index === messageIndex ? applyServerRun(message, durableRun) : message))
        } catch { /* 保留服务端 SSE 投影，直到下一次数据加载 */ }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '恢复任务失败'
      setMessages(current => current.map((item, index) => index === messageIndex ? { ...item, status: 'error', error: true, retryable: true, text: message } : item))
      setNotice('任务恢复失败，请重试确认操作。')
    } finally {
      setRunning(false)
    }
  }, [messages, running])

  const updateAssistant = useCallback((update: (message: AiMessage) => AiMessage) => {
    setMessages(current => {
      const next = [...current]; const target = next[next.length - 1]
      if (!target || target.role !== 'assistant') return current
      next[next.length - 1] = update(target)
      return next
    })
  }, [])

  const updateMessage = useCallback((index: number, update: (message: AiMessage) => AiMessage) => {
    setMessages(current => {
      if (index < 0 || index >= current.length) return current
      const next = [...current]
      next[index] = update(next[index])
      return next
    })
  }, [])

  const setProjectId = useCallback((nextProjectId?: string) => setActiveProjectId(nextProjectId), [])
  return { tasks, taskId, threadId, messages, running, runningTaskId, notice, send, newChat, selectTask, resolveConfirmation, setProjectId, setNotice, updateAssistant, updateMessage }
}
